import React, { useRef, useState } from 'react';
import { Steps, Tabs, message, Button, Space, Result, Modal, List, Popconfirm, Grid } from 'antd';
import { FormOutlined, EditOutlined, RocketOutlined, ReloadOutlined, PlusOutlined, UnorderedListOutlined, TeamOutlined, BarChartOutlined, SaveOutlined, FolderOpenOutlined, DeleteOutlined } from '@ant-design/icons';
import MetaAdsBriefForm from '../components/metaAds/MetaAdsBriefForm';
import MetaAdsDraftReview from '../components/metaAds/MetaAdsDraftReview';
import MetaAdsCampaignsList from '../components/metaAds/MetaAdsCampaignsList';
import MetaAdsAudiences from '../components/metaAds/MetaAdsAudiences';
import MetaAdsDashboard from '../components/metaAds/MetaAdsDashboard';
import MetaAdsAIControls from '../components/metaAds/MetaAdsAIControls';
import MetaAdsStrategyReview from '../components/metaAds/MetaAdsStrategyReview';
import useAudiencesMeta from '../hooks/useAudiencesMeta';
import { metaAdsApi } from '../utils/api';

/**
 * Page de création et de suivi des campagnes Meta Ads assistées par IA.
 *
 * Onglet « Nouvelle campagne » — parcours en trois étapes :
 *  1. Brief    : objectif, produits, budget journalier, durée
 *  2. Revue    : brouillon généré par l'IA, entièrement éditable
 *  3. Création : publication sur Meta en statut PAUSE
 *
 * Onglet « Mes campagnes » — historique, activation/pause et performances.
 *
 * La page se limite à l'orchestration : chaque étape est un composant indépendant.
 */

const MetaAdsCampaigns = () => {
  // Mobile (< md) : étapes compactes en ligne ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [draft, setDraft] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [brief, setBrief] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [campagnePubliee, setCampagnePubliee] = useState(null);
  const [ongletActif, setOngletActif] = useState('nouvelle');
  const [draftId, setDraftId] = useState(null);
  const [brouillons, setBrouillons] = useState([]);
  const [brouillonsOuverts, setBrouillonsOuverts] = useState(false);
  const [sauvegardeBrouillon, setSauvegardeBrouillon] = useState(false);
  // Référence synchrone de l'identifiant, utilisée quand deux sauvegardes automatiques
  // se suivent avant qu'un nouveau rendu React ait eu le temps de se produire.
  const draftIdRef = useRef(null);
  // Choix temporaire, appliqué à chaque requête IA Meta Ads de cette page uniquement.
  const [aiOptions, setAiOptions] = useState({ provider: 'openai', model: 'gpt-5.6-terra' });

  // Liste d'audiences partagée par l'onglet « Audiences » et la revue du brouillon.
  // Les onglets Ant Design restent montés une fois affichés : sans cette source unique,
  // la revue conserverait indéfiniment la liste chargée à la génération du brouillon.
  const { audiences, loading: audiencesLoading, rafraichir: rafraichirAudiences } = useAudiencesMeta();

  /**
   * Recharge les audiences en revenant sur l'onglet de création : une audience passée
   * « prête » chez Meta, ou créée entre-temps, devient immédiatement sélectionnable.
   * @param {string} cle - Onglet demandé
   */
  const changerOnglet = (cle) => {
    setOngletActif(cle);
    if (cle === 'nouvelle') rafraichirAudiences();
  };

  /**
   * Envoie le brief au serveur et récupère d'abord la stratégie IA à valider.
   * @param {Object} valeurs - Données du formulaire de brief
   */
  const genererBrouillon = async (valeurs) => {
    try {
      setGenerating(true);
      // Le brief est sauvegardé avant l'appel IA : il reste reprenable même si la
      // génération échoue, est interrompue ou si l'utilisateur quitte la page.
      await sauvegarderEtape('brief', { brief: valeurs, strategy: null, draft: null, aiOptions }, true);
      const { data } = await metaAdsApi.generateStrategy({ ...valeurs, aiOptions });
      setBrief(valeurs);
      setStrategy(data.data);
      await sauvegarderEtape('strategy', { brief: valeurs, strategy: data.data, draft: null, aiOptions }, true);
      message.success('Stratégie générée : validez-la avant de créer les annonces.');
    } catch (error) {
      console.error('Erreur lors de la génération du brouillon Meta Ads:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la génération du brouillon');
    } finally {
      setGenerating(false);
    }
  };

  /** Génère les créas localisées après validation explicite de la stratégie. */
  const genererCreas = async () => {
    try {
      setGenerating(true);
      const { data } = await metaAdsApi.generateDraft({ ...brief, strategy, aiOptions });
      setDraft(data.data);
      setStrategy(null);
      await sauvegarderEtape('review', { brief, strategy: null, draft: data.data, aiOptions }, true);
      message.success('Annonces générées : vérifiez-les avant publication.');
    } catch (error) {
      message.error(error.response?.data?.message || 'Erreur lors de la génération des annonces');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Crée la campagne sur Meta à partir du brouillon validé (statut PAUSE).
   * @param {Object} brouillonValide
   */
  const publierCampagne = async (brouillonValide) => {
    try {
      setPublishing(true);
      const { data } = await metaAdsApi.publish(brouillonValide);
      setCampagnePubliee(data.data);
      setDraft(null);
      setDraftId(null);
      draftIdRef.current = null;
      message.success(data.message);
    } catch (error) {
      console.error('Erreur lors de la publication de la campagne Meta:', error);
      message.error(error.response?.data?.message || 'Erreur lors de la création de la campagne');
    } finally {
      setPublishing(false);
    }
  };

  /** Réinitialise le parcours pour créer une nouvelle campagne */
  const recommencer = () => {
    setDraft(null);
    setStrategy(null);
    setBrief(null);
    setCampagnePubliee(null);
    setDraftId(null);
    draftIdRef.current = null;
  };

  /** Enregistre une étape précise, automatiquement ou à la demande, sans appel à l'IA. */
  const sauvegarderEtape = async (stage, contenu, automatique = false) => {
    try {
      if (!automatique) setSauvegardeBrouillon(true);
      const { data } = await metaAdsApi.saveCampaignDraft({
        id: draftIdRef.current,
        name: contenu.draft?.campaign?.name || contenu.strategy?.summary || 'Campagne Meta à reprendre',
        stage,
        payload: contenu
      });
      draftIdRef.current = data.data.id;
      setDraftId(data.data.id);
      if (automatique) message.success('Étape enregistrée automatiquement.');
      return data.data;
    } catch (error) {
      // Une indisponibilité temporaire de la sauvegarde ne doit jamais empêcher
      // la génération IA demandée par l'utilisateur de se poursuivre.
      if (automatique) {
        console.warn('Sauvegarde automatique Meta Ads indisponible:', error);
        message.warning('Sauvegarde automatique indisponible : vous pourrez enregistrer manuellement ensuite.');
        return null;
      }
      throw error;
    } finally {
      if (!automatique) setSauvegardeBrouillon(false);
    }
  };

  /** Enregistre l'étape déjà générée afin de pouvoir la reprendre sans nouvel appel IA. */
  const sauvegarderBrouillon = async () => {
    const stage = draft ? 'review' : 'strategy';
    if ((stage === 'strategy' && !strategy) || (stage === 'review' && !draft)) return;
    try {
      await sauvegarderEtape(stage, { brief, strategy: stage === 'strategy' ? strategy : null, draft: stage === 'review' ? draft : null, aiOptions });
      message.success('Brouillon enregistré. Vous pourrez le reprendre sans relancer l’IA.');
    } catch (error) {
      message.error(error.response?.data?.message || 'Impossible d’enregistrer le brouillon.');
    }
  };

  /** Charge la liste courte des brouillons avant l'ouverture de la fenêtre de reprise. */
  const ouvrirBrouillons = async () => {
    try {
      const { data } = await metaAdsApi.getCampaignDrafts();
      setBrouillons(data.data || []);
      setBrouillonsOuverts(true);
    } catch (error) {
      message.error('Impossible de charger les brouillons.');
    }
  };

  /** Réhydrate exactement l'étape sauvegardée, sans demander une nouvelle analyse à l'IA. */
  const reprendreBrouillon = async (id) => {
    try {
      const { data } = await metaAdsApi.getCampaignDraft(id);
      const contenu = data.data.payload || {};
      setBrief(contenu.brief || null);
      setStrategy(contenu.strategy || null);
      setDraft(contenu.draft || null);
      setAiOptions(contenu.aiOptions || { provider: 'openai', model: 'gpt-5.6-terra' });
      setDraftId(data.data.id);
      draftIdRef.current = data.data.id;
      setCampagnePubliee(null);
      setBrouillonsOuverts(false);
      setOngletActif('nouvelle');
      message.success('Brouillon rechargé : vous pouvez continuer sans relancer l’IA.');
    } catch (error) {
      message.error('Impossible de recharger ce brouillon.');
    }
  };

  /** Supprime un brouillon non publié puis actualise la liste affichée. */
  const supprimerBrouillon = async (id) => {
    try {
      await metaAdsApi.deleteCampaignDraft(id);
      setBrouillons(precedents => precedents.filter(brouillon => brouillon.id !== id));
      if (draftId === id) {
        setDraftId(null);
        draftIdRef.current = null;
      }
      message.success('Brouillon supprimé.');
    } catch (error) {
      message.error('Impossible de supprimer ce brouillon.');
    }
  };

  // Étape courante du parcours
  const etapeCourante = campagnePubliee ? 2 : ((draft || strategy) ? 1 : 0);

  const contenuNouvelleCampagne = (
    <div className="space-y-6">
      {/* responsive={false} : antd passait en vertical (3 lignes) sous 532px ; on garde une seule ligne compacte, libellés sous les icônes */}
      <Steps
        responsive={false}
        size={isMobile ? 'small' : 'default'}
        labelPlacement={isMobile ? 'vertical' : 'horizontal'}
        current={etapeCourante}
        items={[
          { title: 'Brief', icon: <FormOutlined /> },
          { title: 'Revue', icon: <EditOutlined /> },
          { title: 'Création', icon: <RocketOutlined /> }
        ]}
      />

      {/* Étape 1 : brief */}
      {!draft && !strategy && !campagnePubliee && (
        <MetaAdsBriefForm onGenerate={genererBrouillon} generating={generating} initialValues={brief} />
      )}

      {strategy && !draft && (
        <>
          <Space className="mb-3">
            <Button icon={<SaveOutlined />} loading={sauvegardeBrouillon} onClick={sauvegarderBrouillon}>Enregistrer le brouillon</Button>
          </Space>
          <MetaAdsStrategyReview strategy={strategy} currency={strategy.currency} onContinue={genererCreas} generating={generating} />
        </>
      )}

      {/* Étape 2 : revue et publication */}
      {draft && (
        <>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={recommencer}>
              Recommencer un brief
            </Button>
            <Button icon={<SaveOutlined />} loading={sauvegardeBrouillon} onClick={sauvegarderBrouillon}>
              Enregistrer le brouillon
            </Button>
          </Space>

          <MetaAdsDraftReview
            draft={draft}
            onChange={setDraft}
            onPublish={publierCampagne}
            publishing={publishing}
            audiences={audiences}
            audiencesLoading={audiencesLoading}
            onRafraichirAudiences={rafraichirAudiences}
          />
        </>
      )}

      {/* Étape 3 : confirmation */}
      {campagnePubliee && (
        <Result
          status="success"
          title="Campagne créée sur Meta"
          subTitle={
            <span>
              <strong>{campagnePubliee.name}</strong> a été créée en <strong>statut PAUSE</strong>.
              Aucune dépense n'est engagée tant que vous ne l'activez pas depuis l'onglet « Mes campagnes ».
            </span>
          }
          extra={[
            <Button
              type="primary"
              key="campagnes"
              icon={<UnorderedListOutlined />}
              onClick={() => setOngletActif('campagnes')}
            >
              Voir mes campagnes
            </Button>,
            <Button key="nouvelle" icon={<PlusOutlined />} onClick={recommencer}>
              Créer une autre campagne
            </Button>
          ]}
        />
      )}
    </div>
  );

  return (
    <div className="w-full px-4 py-6 sm:px-6 lg:px-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1">Publicités Meta</h1>
        <p className="text-gray-500">
          Décrivez ce que vous voulez vendre et votre budget : l'IA prépare la campagne,
          vous la validez, elle est créée en pause sur Meta.
        </p>
      </div>

      <MetaAdsAIControls value={aiOptions} onChange={setAiOptions} />

      <Button icon={<FolderOpenOutlined />} onClick={ouvrirBrouillons}>Mes brouillons</Button>

      <Tabs
        activeKey={ongletActif}
        onChange={changerOnglet}
        items={[
          {
            key: 'nouvelle',
            label: <span><PlusOutlined /> Nouvelle campagne</span>,
            children: contenuNouvelleCampagne
          },
          {
            key: 'campagnes',
            label: <span><UnorderedListOutlined /> Mes campagnes</span>,
            children: <MetaAdsCampaignsList />
          },
          {
            key: 'performance',
            label: <span><BarChartOutlined /> Performances</span>,
            children: <MetaAdsDashboard aiOptions={aiOptions} />
          },
          {
            key: 'audiences',
            label: <span><TeamOutlined /> Audiences</span>,
            children: (
              <MetaAdsAudiences
                audiences={audiences}
                loading={audiencesLoading}
                onRafraichir={rafraichirAudiences}
              />
            )
          }
        ]}
      />

      <Modal title="Mes brouillons Meta Ads" open={brouillonsOuverts} footer={null} onCancel={() => setBrouillonsOuverts(false)}>
        <List
          locale={{ emptyText: 'Aucun brouillon enregistré.' }}
          dataSource={brouillons}
          renderItem={brouillon => (
            <List.Item actions={[
              <Button key="resume" type="link" onClick={() => reprendreBrouillon(brouillon.id)}>Reprendre</Button>,
              <Popconfirm key="delete" title="Supprimer ce brouillon ?" onConfirm={() => supprimerBrouillon(brouillon.id)}>
                <Button type="link" danger icon={<DeleteOutlined />}>Supprimer</Button>
              </Popconfirm>
            ]}>
              <List.Item.Meta title={brouillon.name} description={`${brouillon.stage === 'brief' ? 'Brief à analyser' : brouillon.stage === 'strategy' ? 'Stratégie à valider' : 'Annonces à revoir'} · Modifié le ${new Date(brouillon.updated_at).toLocaleString('fr-FR')}`} />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default MetaAdsCampaigns;
